import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { Trash2, Search, FileJson, Download, Plus, Eye, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { AddMeasuresModal } from './AddMeasuresModal';
import { fetchMeasuresList } from '@/lib/admin-cache';

interface Measure {
  measure_id: string;
  measure_title: string;
  measure_type: string;
  measure_frequency: string;
  submission_method: string;
  stratum: string | null;
  year: number;
  inverse: boolean;
  is_active: boolean;
  created_at: string;
  source_file_id: number;
}

/** Row identity for selection/deletion. A measure id is NOT unique in the
 *  library — the same id can carry rows per submission method (R14 lanes),
 *  year, and stratum. Selecting/deleting must target ONE row, never siblings. */
const rowKey = (m: Measure) =>
  `${m.measure_id}|${m.submission_method}|${m.year}|${m.stratum ?? ''}`;

export function MeasureLibraryTable() {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [methodFilter, setMethodFilter] = useState<string>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<Measure | null>(null);
  
  // Bulk selection state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedMeasures, setSelectedMeasures] = useState<Set<string>>(new Set());
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  
  // Add measures modal state
  const [showAddMeasuresModal, setShowAddMeasuresModal] = useState(false);

  // View JSON modal state
  const [viewMeasure, setViewMeasure] = useState<Measure | null>(null);
  const [viewData, setViewData] = useState<any>(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Bulk export state
  const [bulkExporting, setBulkExporting] = useState(false);

  // Fetch measures — shared fetcher so the login-time prefetch
  // (admin-cache.ts prefetchAdminData) warms this exact query.
  const { data: measures, isLoading } = useQuery<Measure[]>({
    queryKey: ['measures'],
    queryFn: fetchMeasuresList,
    // Keep the data for the whole session (default 5-min gcTime would evict
    // it between visits and bring the skeleton back). Refetch still runs on
    // every mount; rows update in place.
    gcTime: 12 * 60 * 60 * 1000,
  });

  // Helper functions for bulk selection (keyed by rowKey — see above)
  const toggleMeasureSelection = useCallback((key: string) => {
    setSelectedMeasures(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);
  
  const selectAllMeasures = useCallback((measures: Measure[]) => {
    setSelectedMeasures(new Set(measures.map(rowKey)));
  }, []);
  
  const clearSelection = useCallback(() => {
    setSelectedMeasures(new Set());
  }, []);
  
  const exitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    clearSelection();
  }, [clearSelection]);

  // Delete measure mutation — scoped to the ROW's submission method + year so
  // deleting e.g. 112 (Medicare CQM) never touches 112 (MIPS CQM).
  const deleteMeasureRow = async (measure: Measure) => {
    const token = localStorage.getItem('auth_token');
    const params = new URLSearchParams({
      submissionMethod: measure.submission_method,
      year: measure.year.toString(),
    });
    const response = await fetch(`${apiUrl}/api/measures/${measure.measure_id}?${params}`, {
      method: 'DELETE',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });
    if (!response.ok) {
      throw new Error(`Failed to delete measure ${measure.measure_id} (${measure.submission_method}, ${measure.year})`);
    }
    return response.json();
  };

  const deleteMutation = useMutation({
    mutationFn: deleteMeasureRow,
    onSuccess: () => {
      toast({
        title: 'Measure deleted',
        description: 'The measure has been removed from the library.'
      });
      queryClient.invalidateQueries({ queryKey: ['measures'] });
      setDeleteConfirm(null);
    },
    onError: (error) => {
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Failed to delete measure',
        variant: 'destructive'
      });
    }
  });

  // Bulk delete mutation (per row — method/year scoped like single delete)
  const bulkDeleteMutation = useMutation({
    mutationFn: async (rows: Measure[]) => {
      const results = await Promise.all(
        rows.map(async (row) => {
          try {
            await deleteMeasureRow(row);
            return { measureId: row.measure_id, success: true };
          } catch (error) {
            return { measureId: row.measure_id, success: false, error };
          }
        })
      );
      
      const failures = results.filter(r => !r.success);
      if (failures.length > 0) {
        throw new Error(`Failed to delete ${failures.length} measure(s)`);
      }
      
      return results;
    },
    onSuccess: (results) => {
      const successCount = results.filter(r => r.success).length;
      toast({
        title: 'Measures deleted',
        description: `Successfully deleted ${successCount} measure${successCount === 1 ? '' : 's'}.`
      });
      queryClient.invalidateQueries({ queryKey: ['measures'] });
      exitSelectionMode();
      setShowBulkDeleteDialog(false);
    },
    onError: (error) => {
      toast({
        title: 'Bulk delete failed',
        description: error instanceof Error ? error.message : 'Failed to delete measures',
        variant: 'destructive'
      });
    }
  });

  // Years present in the library (newest first) for the year filter
  const availableYears = [...new Set((measures ?? []).map(m => m.year))]
    .sort((a, b) => b - a);

  // Collection types (submission methods) present in the library, alphabetical
  const availableMethods = [...new Set((measures ?? []).map(m => m.submission_method))]
    .filter(Boolean)
    .sort();

  // Filter measures based on search + year + collection type
  const filteredMeasures = measures?.filter(measure =>
    (yearFilter === 'all' || measure.year === Number(yearFilter)) &&
    (methodFilter === 'all' || measure.submission_method === methodFilter) &&
    (measure.measure_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
     measure.measure_title.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  // Selections survive filter changes in state, but every action (count,
  // export, delete) applies ONLY to selections currently VISIBLE under the
  // active filters — a select-all under a wider filter must not silently act
  // on rows the user can no longer see.
  const effectiveSelectedRows = filteredMeasures.filter(m => selectedMeasures.has(rowKey(m)));

  // View measure logic JSON in-app (reuses the export endpoint)
  const handleView = async (measure: Measure) => {
    setViewMeasure(measure);
    setViewData(null);
    setViewLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${apiUrl}/api/measures/${measure.measure_id}/export`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      if (!response.ok) {
        throw new Error('Failed to load measure');
      }
      const data = await response.json();
      setViewData(data);
    } catch (error) {
      toast({
        title: 'Failed to load',
        description: 'Could not load the measure logic.',
        variant: 'destructive'
      });
      setViewMeasure(null);
    } finally {
      setViewLoading(false);
    }
  };

  // Export measure as JSON
  const handleExport = async (measure: Measure) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${apiUrl}/api/measures/${measure.measure_id}/export`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to export measure');
      }
      
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Create a more descriptive filename with measure ID, year, and export date
      const exportDate = new Date().toISOString().split('T')[0];
      a.download = `measure_${measure.measure_id}_${measure.year}_export_${exportDate}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: 'Measure exported',
        description: `Downloaded complete measure definition for ${measure.measure_id}`
      });
    } catch (error) {
      toast({
        title: 'Export failed',
        description: 'Failed to export measure definition',
        variant: 'destructive'
      });
    }
  };

  // Bulk export: bundle the selected measures into ONE JSON file — an ARRAY
  // of measure definitions, the exact shape POST /api/measures/upload expects
  // (measureValidator requires an array), so the file round-trips through
  // "Add Measures" on another environment.
  const handleBulkExport = async () => {
    // Export endpoint is measure-level (latest active definition) — dedupe ids
    // when multiple lanes of the same measure are selected.
    const ids = [...new Set(effectiveSelectedRows.map(m => m.measure_id))];
    if (ids.length === 0) return;
    setBulkExporting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const results = await Promise.all(ids.map(async (measureId) => {
        const response = await fetch(`${apiUrl}/api/measures/${measureId}/export`, {
          headers: { 'Authorization': token ? `Bearer ${token}` : '' }
        });
        if (!response.ok) throw new Error(`Failed to export measure ${measureId}`);
        return response.json();
      }));

      const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const exportDate = new Date().toISOString().split('T')[0];
      a.download = `measures_export_${ids.length}_${exportDate}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Measures exported',
        description: `Downloaded ${results.length} measure definition${results.length === 1 ? '' : 's'} in one file (re-uploadable via Add Measures).`
      });
    } catch (error) {
      toast({
        title: 'Export failed',
        description: error instanceof Error ? error.message : 'Failed to export selected measures',
        variant: 'destructive'
      });
    } finally {
      setBulkExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search measures by ID or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {availableYears.map(year => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={methodFilter} onValueChange={setMethodFilter}>
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="Collection Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {availableMethods.map(method => (
              <SelectItem key={method} value={method}>
                {method}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAddMeasuresModal(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Measures
        </Button>
        <Button
          variant={isSelectionMode ? "default" : "outline"}
          size="sm"
          onClick={() => {
            if (isSelectionMode) {
              exitSelectionMode();
            } else {
              setIsSelectionMode(true);
            }
          }}
        >
          <input
            type="checkbox"
            className="rounded border-gray-300 pointer-events-none mr-2"
            checked={isSelectionMode}
            readOnly
          />
          Select
        </Button>
        <Badge variant="secondary" className="px-3 py-1">
          {filteredMeasures.length} measures
        </Badge>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {isSelectionMode && (
                <TableHead className="text-center w-12">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={effectiveSelectedRows.length > 0 && effectiveSelectedRows.length === filteredMeasures.length}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = effectiveSelectedRows.length > 0 && effectiveSelectedRows.length < filteredMeasures.length;
                      }
                    }}
                    onChange={(e) => {
                      if (e.target.checked) {
                        selectAllMeasures(filteredMeasures);
                      } else {
                        clearSelection();
                      }
                    }}
                  />
                </TableHead>
              )}
              <TableHead>Measure ID</TableHead>
              <TableHead className="min-w-[200px]">Title</TableHead>
              <TableHead>Stratum</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead>Submission</TableHead>
              <TableHead>Year</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredMeasures.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isSelectionMode ? 9 : 8} className="text-center text-muted-foreground py-8">
                  {searchTerm || yearFilter !== 'all' || methodFilter !== 'all'
                    ? 'No measures found matching your filters'
                    : 'No measures uploaded yet'}
                </TableCell>
              </TableRow>
            ) : (
              filteredMeasures.map((measure, index) => (
                <TableRow key={`${measure.measure_id}-${measure.stratum || 'none'}-${measure.year}-${index}`}>
                  {isSelectionMode && (
                    <TableCell className="text-center w-12">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                        checked={selectedMeasures.has(rowKey(measure))}
                        onChange={() => toggleMeasureSelection(rowKey(measure))}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <FileJson className="h-4 w-4 text-muted-foreground" />
                      {measure.measure_id}
                    </div>
                  </TableCell>
                  <TableCell className="2xl:max-w-xs whitespace-normal 2xl:truncate 2xl:whitespace-nowrap break-words min-w-[200px]" title={measure.measure_title}>
                    {measure.measure_title}
                  </TableCell>
                  <TableCell>
                    {measure.stratum ? (
                      <Badge variant="outline" className="text-xs">
                        {measure.stratum}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell>{measure.measure_frequency}</TableCell>
                  <TableCell>
                    <Badge variant={measure.submission_method === 'eCQM' ? 'default' : 'secondary'}>
                      {measure.submission_method}
                    </Badge>
                  </TableCell>
                  <TableCell>{measure.year}</TableCell>
                  <TableCell>
                    <Badge variant={measure.is_active ? 'success' : 'secondary'}>
                      {measure.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleView(measure)}
                        title="View measure logic"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleExport(measure)}
                        title="Export measure definition"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteConfirm(measure)}
                        className="text-destructive hover:text-destructive"
                        title="Delete measure"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Bulk action bar — counts/actions cover only VISIBLE selections */}
      {isSelectionMode && effectiveSelectedRows.length > 0 && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-700">
              {effectiveSelectedRows.length} row{effectiveSelectedRows.length === 1 ? '' : 's'} selected
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkExport}
                disabled={bulkExporting}
              >
                {bulkExporting
                  ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  : <Download className="h-4 w-4 mr-1" />}
                Export Selected ({[...new Set(effectiveSelectedRows.map(m => m.measure_id))].length})
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowBulkDeleteDialog(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete Selected ({effectiveSelectedRows.length})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Measure?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete measure{' '}
              <strong>{deleteConfirm?.measure_id}</strong>
              {deleteConfirm ? ` (${deleteConfirm.submission_method}, ${deleteConfirm.year})` : ''}?
              Only this submission method&apos;s definition is removed — other lanes of the
              same measure are untouched. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Measure
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent className="max-h-[90vh] flex flex-col">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Multiple Measures?</AlertDialogTitle>
            <AlertDialogDescription className="flex-1 overflow-hidden flex flex-col">
              <div>Are you sure you want to delete {effectiveSelectedRows.length} selected row{effectiveSelectedRows.length === 1 ? '' : 's'}? Only the listed submission-method lanes are removed. This action cannot be undone.</div>
              {effectiveSelectedRows.length > 0 && (
                <div className="mt-3 flex-1 overflow-hidden flex flex-col">
                  <div className="text-sm font-medium mb-2">Rows to be deleted:</div>
                  <div className="overflow-y-auto max-h-[50vh] pr-2">
                    <ul className="text-sm space-y-2">
                      {effectiveSelectedRows.map(measure => (
                        <li key={rowKey(measure)} className="flex items-start gap-2">
                          <FileJson className="h-3 w-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">{measure.measure_id}</span>
                            <span className="text-muted-foreground ml-1">
                              ({measure.submission_method}, {measure.year})
                            </span>
                            <span className="text-muted-foreground ml-1 break-words">
                              - {measure.measure_title}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                bulkDeleteMutation.mutate(effectiveSelectedRows);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete {effectiveSelectedRows.length} Row{effectiveSelectedRows.length === 1 ? '' : 's'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Measures Modal */}
      <AddMeasuresModal 
        open={showAddMeasuresModal} 
        onOpenChange={setShowAddMeasuresModal} 
      />

      {/* View Measure Logic Modal */}
      <Dialog
        open={!!viewMeasure}
        onOpenChange={(open) => {
          if (!open) {
            setViewMeasure(null);
            setViewData(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileJson className="h-4 w-4" />
              Measure {viewMeasure?.measure_id} — Logic
            </DialogTitle>
            <DialogDescription>
              {viewMeasure?.measure_title}
              {viewMeasure ? ` · ${viewMeasure.year} · ${viewMeasure.submission_method}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto rounded-md border bg-muted/40 min-h-[200px]">
            {viewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <pre className="text-xs p-4 whitespace-pre-wrap break-words font-mono">
                {viewData ? JSON.stringify(viewData, null, 2) : 'No data'}
              </pre>
            )}
          </div>
          <DialogFooter>
            {viewMeasure && (
              <Button variant="outline" size="sm" onClick={() => handleExport(viewMeasure)}>
                <Download className="h-4 w-4 mr-2" />
                Download JSON
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setViewMeasure(null);
                setViewData(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
